import {withSidebar} from "../hoc/withSidebar";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import React, {useEffect, useState} from "react";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import CardList from "../custom/containers/cardList";
import ERPSRecallRejectSearch, { getDateLimits, billerDetails } from "../custom/ERPSRecallRejectSearch";
import {formatDate, setFinancialInstitutionUIData, getComponentAccess} from "../../utils/helper";
import moment from "moment/moment";
import actions from "../../actions";
import { transactionSearchHeading } from "../../constants/TransactionSearchConstants.js";
import {RoleType} from "../../constants/sharedConstants";
import { ResponsiveBox, Typography } from "../primitives";

const TraceHistory = (props) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        history,
        location: { pathname, state },
    } = props;

    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [rowPerPage, setRowPerPage] = useState(100);
    const [dataCount, setDataCount] = useState(0);

    const user = useSelector((state) => state.loginUserReducer.user);
    const [isBillerRole] = useState(user.role.role_type === RoleType.BILLER_ADMIN.type || user.role.role_type === RoleType.BILLER_USER.type);

    const componentAccess = state?.componentAccess
	    	|| getComponentAccess( { function_type_code: "TH", label_type_code: "TE" }, user );

    const tqResponse = useSelector(
        (state) => state.traceManagementReducer
    );

    const dates = getDateLimits("TH");

    const initialSearchValues = {
        paymentFromDate: dates.aMonthAgo,
        paymentToDate: dates.today,
        selectedFiNumber: "0",
        searchBy: billerDetails[0].value,   // "billerId"
        billerId:  isBillerRole ? user.billerNonVolatileDto.biller_id : null, // internal biller id for Biller Role
        bmoBillerId: "",  // external biller id entered by the user
        billerName: "",
        billerCustomerNumber: "",
        fiCustomerName: ""
    };

    const [searchValues, setSearchValues] = useState(initialSearchValues);
    const [actualCriteria, setActualCriteria] = useState(
        state?.fromDetailPage ? tqResponse?.traceQueueDetails : { ...initialSearchValues, offset: 0, limit: rowPerPage}
    );

    const [traceHistoryData, setTraceHistoryData] = useState( state?.fromDetailPage && tqResponse?.traceQueueData?.data || null );

    const [fiList, setFiList] = useState([]);
    const [fiMap, setFiMap] = useState(null);
    const fiReducer = useSelector( (state) => state.fiReducer );

    useEffect(() => {
        isBillerRole && setFinancialInstitutionUIData(fiReducer?.financialInstitutionList, setFiMap, setFiList, null, user?.languageCode);
    }, [fiReducer.financialInstitutionList, user?.languageCode])

    useEffect(() => {
        if (isBillerRole && !(fiReducer?.financialInstitutionList?.length > 0))
            dispatch(actions.fiActions.getFinancialInstitutionList(t));

        if (state?.fromDetailPage) {
            Object.keys(searchValues).filter(key => tqResponse.traceQueueDetails[key])
                .forEach(key => searchValues[key] = tqResponse?.traceQueueDetails[key]);
            setRowPerPage(actualCriteria.limit);
            setPageNumber(actualCriteria.offset / actualCriteria.limit);
            state.fromDetailPage = null;  // resetting the coming-from-detail-page flag
        } else {  // reset search resutls previously saved in the reducer
            dispatch({
                type: "GET_TRACE_QUEUE_SUCCESS",
                data: { data: null, traceQueueDetails: null },
            });
        }
    }, []);

    useEffect(() => {
        if (tqResponse && loading === false) {
            const traceHistory = tqResponse?.traceQueueData?.data;
            if (traceHistory?.length) {
                for (let i = 0; i < traceHistory.length; i++) {
                    traceHistory[i].seqNo = i + 1 + actualCriteria.offset;
                    traceHistory[i].id = traceHistory[i].trace_id;
                }
            }
            setDataCount(tqResponse.traceQueueData?.count || 0);
            setTraceHistoryData(traceHistory);
        }
    }, [loading]);


    const handleReset = () => {
        setSearchValues({ ...initialSearchValues });
        setActualCriteria({ ...initialSearchValues, offset: 0 });
        setPageNumber(0);
        setErrorMessage(null);

    };

    const handleSearch = (groomedValues) => {
        if (pageNumber)
            setPageNumber(0);

        if (groomedValues.paymentFromDate)
            groomedValues.paymentFromDate = formatDate(moment(groomedValues.paymentFromDate).format());
        if (groomedValues.paymentToDate)
            groomedValues.paymentToDate = formatDate(moment(groomedValues.paymentToDate).format());
        groomedValues.limit = actualCriteria.limit;
        groomedValues.offset = 0;

        if (!isBillerRole) {
            if (groomedValues.searchBy === billerDetails[0].value) {  // "billerId"
                delete groomedValues["billerName"];
                groomedValues.billerId = groomedValues.bmoBillerId;
            } else if (groomedValues.searchBy === billerDetails[1].value) { //  "billerName"
                delete groomedValues["bmoBillerId"];
                delete groomedValues["billerId"];
            }
        }
        groomedValues.transactionStatus = "U";
        setActualCriteria({ ...groomedValues });

        loadResults(groomedValues, " (search)");
    };

    const loadResults = (queryParams, prompt) => {
        if (errorMessage)
            setErrorMessage(null);
        setLoading(true);

        dispatch(
            actions.traceManagementActions.searchTraceQueue(
                queryParams,
                { ...props, history, setLoading, setErrorMessage }
            )
        );
    }

    const paginate = (values) => {
        actualCriteria.offset = values.startOffset;
        if (actualCriteria.limit !== (values.endOffset - values.startOffset)) // if the user changed the number of rows per page
            actualCriteria.limit = values.endOffset - values.startOffset;

        loadResults(actualCriteria, " (paginate)");
    };

    return (
        <div role="main">
            <ResponsiveBox ml={3}>
                <ERPSRecallRejectSearch
                    pageType="TH"
                    pageTitle={t("erps.tracehistory.page.title")}
                    pageDescription={t("erps.transactionEnquiry.page.description")}
                    searchValues={searchValues}
                    setSearchValues={setSearchValues}
                    dates={dates}
                    loading={loading}
                    errorMessage={errorMessage}
                    componentAccess={componentAccess}
                    handleReset={handleReset}
                    handleSearch={handleSearch}
                    financialInstitutionList={fiList}
                />
                {(traceHistoryData !== null && traceHistoryData !== undefined && Array.isArray(traceHistoryData) && traceHistoryData.length > 0) ? (
                    <>
                        <ResponsiveBox className="manageUsers_table_visible">
                            <ResponsiveDataGrid
                                data={{
                                    rows: traceHistoryData,
                                    columns: transactionSearchHeading("TH", isBillerRole ? fiMap : null, "/trace-history/trace-history-detail"),
                                    count: dataCount,
                                }}
                                sumOfColumnWidth="true"
                                initialLoading={loading}
                                pageNumber={pageNumber}
                                setPage={setPageNumber}
                                rowPerPage={rowPerPage}
                                onAnyPaginationAction={paginate}
                                tableLabelText={t("erps.transactionEnquiry.page.title")}
                                hideFooterPagination={false}
                                pageName={"transaction-enquiry"}
                            />
                        </ResponsiveBox>
                        <ResponsiveBox className="manageUsers_card_visible">
                            <CardList
                                data={{
                                    rows: traceHistoryData,
                                    columns: transactionSearchHeading("TH", isBillerRole ? fiMap : null, "/trace-history/trace-history-detail"),
                                    count: traceHistoryData?.count || 0
                                }}
                                onAnyPaginationAction={paginate}
                                hideFooterPagination={false}
                                pageName={"trace-history"}
                                pageNumber={pageNumber}
                                setPage={setPageNumber}
                                rowPerPage={rowPerPage}
                            />
                        </ResponsiveBox>
                    </>
                ) : (
                    <ResponsiveBox mt={1}>
                        <Typography variant="body1">
                            {(traceHistoryData?.length === 0)
                                ? (`0 ${t("erps.transactionEnquiry.page.resultfound")}`)
                                : (t("erps.availabledocuments.searchuser.notstarted", "Search not started"))
                            }
                        </Typography>
                    </ResponsiveBox>
                )}
            </ResponsiveBox>
        </div>
    );

}

export default withSidebar(TraceHistory);